import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import {
  DataGrid,
  gridClasses,
  GridColDef,
  GridRenderCellParams,
  GridRowSelectionModel,
  gridPaginationModelSelector,
  useGridApiContext,
  useGridSelector,
} from "@mui/x-data-grid";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import FirstPageRoundedIcon from "@mui/icons-material/FirstPageRounded";
import LastPageRoundedIcon from "@mui/icons-material/LastPageRounded";
import KeyboardArrowLeftRoundedIcon from "@mui/icons-material/KeyboardArrowLeftRounded";
import KeyboardArrowRightRoundedIcon from "@mui/icons-material/KeyboardArrowRightRounded";
import MedicationIcon from "@mui/icons-material/Medication";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import Typography from "@mui/material/Typography";
import { useNavigate } from "react-router-dom";
import { routeConfigs } from "../../navigation/routeConfig";
import { useQuery } from "@apollo/client";
import { SELL_LIST_PAGINATED } from "../../queries/product/productTypeDefs";
import { UserContext } from "../../hooks/userContext";
import { TextField, debounce, IconButton, InputAdornment } from "@mui/material";
import moment from "moment";
import { formatPrice } from "../../components/utils/utils";
import axios from "axios";
import { pageTitleSx } from "../../constants/designTokens";
import { BrandLoaderOverlay } from "../../components/shared/BrandLoader/BrandLoader";

const INK = "#1E293B";
const INK_SOFT = "#475569";
const LINE = "#E2E8F0";
const LINE_SOFT = "#F1F5F9";
const PRIMARY = "#2563EB";
const PRIMARY_DARK = "#1D4ED8";
const BODY_FONT = '"Source Sans 3", sans-serif';
const MONO_FONT = '"Spline Sans Mono", monospace';

interface ISellListProps {
  isAdmin?: boolean;
}

const pageButtonSx = (selected: boolean) => ({
  minWidth: 32,
  width: 32,
  height: 32,
  borderRadius: "8px",
  fontWeight: 600,
  fontFamily: BODY_FONT,
  p: 0,
  lineHeight: 1,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  ...(selected
    ? {
        backgroundColor: PRIMARY,
        color: "#FFFFFF",
        boxShadow: "none",
        "&:hover": { backgroundColor: PRIMARY_DARK, boxShadow: "none" },
      }
    : {
        color: INK_SOFT,
        borderColor: LINE,
        "&:hover": { borderColor: INK_SOFT, backgroundColor: LINE_SOFT },
      }),
});

const SellGridPagination = (props: any) => {
  const apiRef = useGridApiContext();
  const paginationModel = useGridSelector(apiRef, gridPaginationModelSelector);
  const totalPages = Math.max(Math.ceil((props.rowCount ?? 0) / (paginationModel.pageSize || 10)), 1);
  const currentPage = paginationModel.page + 1;
  const windowStart = Math.max(1, Math.min(currentPage - 1, totalPages - 2));
  const windowEnd = Math.min(totalPages, windowStart + 2);
  const visiblePages = Array.from({ length: windowEnd - windowStart + 1 }, (_, index) => windowStart + index);
  const shouldShowLastPage = totalPages > 3 && windowEnd < totalPages;
  const isFirstPage = currentPage <= 1;
  const isLastPage = currentPage >= totalPages;

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        width: "100%",
        px: 2,
        py: 0.5,
      }}
    >
      <Typography sx={{ fontFamily: MONO_FONT, fontSize: "11px", color: INK_SOFT }}>
        Page {currentPage} of {totalPages}
      </Typography>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
        <IconButton size="small" sx={{ color: INK_SOFT }} onClick={() => apiRef.current.setPage(0)} disabled={isFirstPage}>
          <FirstPageRoundedIcon fontSize="small" />
        </IconButton>
        <IconButton size="small" sx={{ color: INK_SOFT }} onClick={() => apiRef.current.setPage(currentPage - 2)} disabled={isFirstPage}>
          <KeyboardArrowLeftRoundedIcon fontSize="small" />
        </IconButton>

        {visiblePages.map((page) => {
          const isSelected = currentPage === page;
          return (
            <Button
              key={page}
              size="small"
              variant={isSelected ? "contained" : "outlined"}
              onClick={() => apiRef.current.setPage(page - 1)}
              sx={pageButtonSx(isSelected)}
            >
              {page}
            </Button>
          );
        })}
        {shouldShowLastPage && (
          <>
            <Typography sx={{ px: 0.5, color: INK_SOFT, fontSize: 13 }}>
              ...
            </Typography>
            <Button
              size="small"
              variant={currentPage === totalPages ? "contained" : "outlined"}
              onClick={() => apiRef.current.setPage(totalPages - 1)}
              sx={pageButtonSx(currentPage === totalPages)}
            >
              {totalPages}
            </Button>
          </>
        )}

        <IconButton size="small" sx={{ color: INK_SOFT }} onClick={() => apiRef.current.setPage(currentPage)} disabled={isLastPage}>
          <KeyboardArrowRightRoundedIcon fontSize="small" />
        </IconButton>
        <IconButton size="small" sx={{ color: INK_SOFT }} onClick={() => apiRef.current.setPage(totalPages - 1)} disabled={isLastPage}>
          <LastPageRoundedIcon fontSize="small" />
        </IconButton>
      </Box>
    </Box>
  );
};

interface IStatusStyle {
  color: string;
  bg: string;
  dot: string;
}

const STATUS_STYLES: Record<string, IStatusStyle> = {
  Active: { color: "#1E7B3C", bg: "#DFF2E5", dot: "#2E9E57" },
  Inactive: { color: "#A02121", bg: "#F9E3E3", dot: "#C63A3A" },
  Sold: { color: "#C2410C", bg: "#F4E7E0", dot: "#C2410C" },
  Sealed: { color: "#2E7D32", bg: "#E8F5E9", dot: "#66BB6A" },
  "Non-Sealed": { color: "#B45309", bg: "#FEF3C7", dot: "#F59E0B" },
};

const StatusTag = ({ status }: { status: string }) => {
  const style = STATUS_STYLES[status] || { color: INK_SOFT, bg: LINE_SOFT, dot: "#475569" };
  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        gap: "4px",
        height: 16,
        px: "5px",
        boxSizing: "border-box",
        borderRadius: "4px",
        fontFamily: MONO_FONT,
        fontSize: "9px",
        fontWeight: 600,
        letterSpacing: "0.04em",
        textTransform: "uppercase",
        whiteSpace: "nowrap",
        lineHeight: 1,
        color: style.color,
        backgroundColor: style.bg,
      }}
    >
      <Box sx={{ width: 4, height: 4, borderRadius: "50%", bgcolor: style.dot, flexShrink: 0 }} />
      {status}
    </Box>
  );
};

export const SellList: React.FunctionComponent<ISellListProps> = ({ isAdmin }) => {
  const { id }: any = useContext(UserContext);
  const navigate = useNavigate();
  const [searchInput, setSearchInput] = useState("");
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
  const [currentWhere, setCurrentWhere] = useState<any>(() =>
    isAdmin ? {} : { User: { is: { id: { equals: +id } } } }
  );

  useEffect(() => {
    setCurrentWhere(isAdmin ? {} : { User: { is: { id: { equals: +id } } } });
    setPaginationModel((model) => ({ ...model, page: 0 }));
  }, [isAdmin, id]);

  const { data, loading: listLoading } = useQuery(SELL_LIST_PAGINATED, {
    variables: {
      where: currentWhere,
      orderBy: [{ createdAt: "desc" }],
      skip: paginationModel.page * paginationModel.pageSize,
      take: paginationModel.pageSize,
    },
    skip: !isAdmin && (id == null || id === undefined || Number.isNaN(+id)),
    fetchPolicy: "no-cache",
    pollInterval: 5000,
  });

  const paginatedResult = data?.findManyProductPaginated;
  const lastKnownCount = useRef(0);
  if (paginatedResult?.totalCount != null) {
    lastKnownCount.current = paginatedResult.totalCount;
  }
  const totalRows = lastKnownCount.current;

  const rows = useMemo(() => {
    const products = paginatedResult?.products;
    if (!products) return [];
    return products.map((product: any) => ({
      ...product,
      expirationDate: moment(product.expDate),
      lot: product.lotNumber,
      available: product.quantity,
      packaging: product.quantityPerPack,
      type: product.type === 1 ? "Full" : "Partial",
      sealed: product.packageCondition === 1 ? "Sealed" : "Non-Sealed",
      price: Number(product.price),
      Pharmacy: product.Pharmacy?.legalName,
      PharmacyStatus: product.Pharmacy?.PharmacyStatus?.Status?.key,
    }));
  }, [paginatedResult?.products]);

  const handleScrollHorizontal = () => {
    const currentScrollPos = document.querySelector(".MuiDataGrid-virtualScroller")?.scrollLeft || 0;
    const firstColumnHeader = document.querySelector<HTMLElement>(".stickyCell");
    if (firstColumnHeader) {
      firstColumnHeader.style.transform = `translate3d(${currentScrollPos}px, 0px, 0px)`;
    }
  };

  useEffect(() => {
    let timer: NodeJS.Timeout | undefined;
    const findVirtualScroller = () => {
      const virtualScrollerElement = document.querySelector(".MuiDataGrid-virtualScroller");
      if (!virtualScrollerElement) {
        timer = setTimeout(findVirtualScroller, 100);
      } else {
        virtualScrollerElement.addEventListener("scroll", handleScrollHorizontal);
        return () => {
          virtualScrollerElement.removeEventListener("scroll", handleScrollHorizontal);
        };
      }
    };
    findVirtualScroller();
    return () => {
      timer && clearTimeout(timer);
    };
  }, []);

  const DrugCellRenderer = ({ params }: { params: GridRenderCellParams<any> }) => {
    const [imageUrl, setImageUrl] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(false);

    useEffect(() => {
      const drugNdc = params?.row?.ndc;
      if (!drugNdc) return;
      const formattedNdc = drugNdc.replace(/-/g, "");
      setIsLoading(true);
      const imageUrlApi = `${process.env.REACT_APP_API_URL}/drug-images?ndc=${formattedNdc}`;
      axios
        .get(imageUrlApi)
        .then((response) => {
          setImageUrl(response.data.imageUrl);
        })
        .catch((error) => {
          console.error("Error fetching drug image:", error);
        })
        .finally(() => {
          setIsLoading(false);
        });
    }, []); // Only re-run if the NDC changes

    const thumbSx = {
      width: 36,
      height: 36,
      borderRadius: "7px",
      marginRight: "8px",
      flexShrink: 0,
      overflow: "hidden",
    };

    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          minWidth: 0,
          width: "100%",
          height: "100%",
          py: 0,
        }}
      >
        {isLoading ? (
          <Box sx={{ ...thumbSx, bgcolor: LINE_SOFT }} />
        ) : imageUrl ? (
          <Box
            component="img"
            src={imageUrl}
            alt="Drug"
            sx={{ ...thumbSx, border: `1px solid ${LINE_SOFT}`, objectFit: "cover", display: "block" }}
          />
        ) : (
          <Box
            sx={{
              ...thumbSx,
              bgcolor: "#F1F5F9",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <MedicationIcon sx={{ fontSize: 20, color: PRIMARY }} />
          </Box>
        )}
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Box
            sx={{
              fontWeight: 600,
              fontSize: "13px",
              color: INK,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              lineHeight: 1.15,
            }}
          >
            {params.value}
          </Box>
          <Box
            sx={{
              fontFamily: MONO_FONT,
              fontSize: "10px",
              color: INK_SOFT,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              lineHeight: 1.15,
            }}
          >
            {params.row.ndc}
          </Box>
        </Box>
      </Box>
    );
  };

  const columns: GridColDef[] = [
    {
      field: "drugName",
      headerName: "Drug name",
      editable: false,
      disableColumnMenu: true,
      sortable: true,
      disableExport: true,
      disableReorder: true,
      flex: 2.2,
      minWidth: 210,
      cellClassName: "stickyCol",
      headerClassName: "stickyCell",
      renderCell: (params: GridRenderCellParams<any>) => {
        return <DrugCellRenderer params={params} />;
      },
    },
    {
      field: "Pharmacy",
      headerName: "Seller",
      editable: false,
      disableColumnMenu: true,
      sortable: true,
      filterable: false,
      disableExport: true,
      disableReorder: true,
      flex: 1.2,
      minWidth: 140,
      renderCell: (params: any) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
          <Box component="span" sx={{ overflow: "hidden", textOverflow: "ellipsis" }}>{params.value}</Box>
          {params.row.PharmacyStatus === "EXPIRED" && (
            <Box
              sx={{
                px: "6px",
                py: "2px",
                borderRadius: "4px",
                bgcolor: "#F4E7E0",
                color: "#C2410C",
                fontFamily: MONO_FONT,
                fontSize: "9.5px",
                fontWeight: 600,
                letterSpacing: "0.05em",
                textTransform: "uppercase",
                flexShrink: 0,
              }}
            >
              Expired
            </Box>
          )}
        </Box>
      ),
    },
    {
      field: "lot",
      headerName: "Lot",
      type: "number",
      editable: false,
      disableColumnMenu: true,
      sortable: true,
      flex: 0.6,
      minWidth: 70,
      align: "left",
      headerAlign: "left",
      cellClassName: "monoCell",
    },
    {
      field: "expirationDate",
      headerName: "Expires",
      editable: false,
      disableColumnMenu: true,
      sortable: true,
      flex: 0.7,
      minWidth: 82,
      type: "dateTime",
      valueFormatter: (value) => moment(value).format("MM/YYYY"),
      cellClassName: "monoCell",
    },
    {
      field: "packaging",
      headerName: "Pack size",
      editable: false,
      disableColumnMenu: true,
      sortable: true,
      flex: 0.7,
      minWidth: 85,
      cellClassName: "monoCell",
    },
    {
      field: "sealed",
      headerName: "Sealed",
      editable: false,
      sortable: true,
      disableColumnMenu: true,
      disableExport: true,
      disableReorder: true,
      flex: 0.9,
      minWidth: 108,
      renderCell: (params: any) => <StatusTag status={params.value} />,
    },
    {
      field: "available",
      headerName: "Qty",
      editable: false,
      disableColumnMenu: true,
      sortable: true,
      flex: 0.5,
      minWidth: 60,
      cellClassName: "monoCell",
    },
    {
      field: "price",
      headerName: "Price",
      editable: false,
      disableColumnMenu: true,
      sortable: true,
      flex: 0.8,
      minWidth: 85,
      type: "number",
      align: "left",
      headerAlign: "left",
      cellClassName: "monoStrongCell",
      renderCell: (params: any) => {
        return `$${formatPrice(params.value)}`;
      },
    },
    {
      field: "type",
      headerName: "Type",
      editable: false,
      disableColumnMenu: true,
      sortable: true,
      flex: 0.6,
      minWidth: 70,
    },
    {
      field: "isActive",
      headerName: "Status",
      editable: false,
      sortable: true,
      disableColumnMenu: true,
      disableExport: true,
      disableReorder: true,
      flex: 0.9,
      minWidth: 100,
      renderCell: (params: any) => {
        const status = !params.value ? "Inactive" : params.row.available > 0 ? "Active" : "Sold";
        return <StatusTag status={status} />;
      },
    },
    {
      field: "action",
      headerName: "",
      editable: false,
      sortable: false,
      disableColumnMenu: true,
      width: 56,
      renderCell: (params: GridRenderCellParams<Date>) => (
        <Box className="rowActions" sx={{ flex: 1, display: "flex", justifyContent: "end", alignItems: "center", pr: 1 }}>
          <IconButton
            size="small"
            aria-label="Edit posting"
            onClick={() => navigate(`/sell/${params.id}`)}
            sx={{
              color: INK_SOFT,
              borderRadius: "8px",
              transition: "transform .18s cubic-bezier(0.34, 1.56, 0.64, 1), color .15s ease, background-color .15s ease",
              "& .MuiSvgIcon-root": { transition: "transform .18s ease" },
              "&:hover": { color: PRIMARY, bgcolor: "#F1F5F9", transform: "scale(1.15)" },
              "&:hover .MuiSvgIcon-root": { transform: "rotate(-10deg)" },
              "&:active": { transform: "scale(0.92)" },
              "@media (prefers-reduced-motion: reduce)": {
                transition: "none",
                "&:hover": { transform: "none" },
                "&:hover .MuiSvgIcon-root": { transform: "none" },
              },
            }}
          >
            <EditOutlinedIcon fontSize="small" />
          </IconButton>
        </Box>
      ),
    },
  ];

  const search = useCallback(
    debounce((value: string) => {
      if (!value) {
        setCurrentWhere(isAdmin ? {} : { User: { is: { id: { equals: +id } } } });
        setPaginationModel((model) => ({ ...model, page: 0 }));
        return;
      }

      const andQuery: any = isAdmin ? [] : [{ User: { is: { id: { equals: +id } } } }];

      if (isAdmin) {
        andQuery.push({
          OR: [
            {
              drugName: {
                contains: value,
                mode: "insensitive",
              },
            },
            {
              ndc: {
                contains: value,
                mode: "insensitive",
              },
            },
            {
              Pharmacy: {
                is: {
                  legalName: {
                    contains: value,
                    mode: "insensitive",
                  },
                },
              },
            },
          ],
        });
      } else {
        andQuery.push({
          OR: [
            {
              drugName: {
                contains: value,
                mode: "insensitive",
              },
            },
            {
              ndc: {
                contains: value,
                mode: "insensitive",
              },
            },
          ],
        });
      }

      setCurrentWhere({ AND: andQuery });
      setPaginationModel((model) => ({ ...model, page: 0 }));
    }, 1000),
    [isAdmin, id]
  );

  const handleSearch = (value: string) => {
    setSearchInput(value);
    search(value);
  };

  return (
    <Box
      sx={{
        bgcolor: "#FFFFFF",
        border: `1px solid ${LINE}`,
        borderRadius: "14px",
        boxShadow: "0 1px 2px rgba(15,23,42,0.06), 0 8px 24px rgba(15,23,42,0.07)",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        fontFamily: BODY_FONT,
        flex: 1,
        minHeight: 0,
        height: "100%",
      }}
    >
      <Box
        sx={{
          px: 2.5,
          py: 0.75,
          display: "flex",
          flexWrap: "wrap",
          gap: 1.5,
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: `1px solid ${LINE}`,
          minHeight: 44,
        }}
      >
        <Box>
          <Typography component="h1" sx={pageTitleSx}>
            {`${isAdmin ? "" : "My "}Postings`}
          </Typography>
        </Box>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, alignItems: "center" }}>
          <TextField
            type="text"
            placeholder={`Search by Drug name or ${isAdmin ? "Seller" : "NDC"}…`}
            value={searchInput}
            onChange={(event: any) => handleSearch(event.target.value)}
            size="small"
            sx={{
              minWidth: { xs: "100%", sm: 320 },
              "& .MuiOutlinedInput-root": {
                borderRadius: "8px",
                fontFamily: BODY_FONT,
                fontSize: 14,
                "& fieldset": { borderColor: LINE },
                "&:hover fieldset": { borderColor: "#475569" },
                "&.Mui-focused fieldset": { borderColor: PRIMARY, borderWidth: "1.5px" },
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchRoundedIcon sx={{ fontSize: 18, color: "#475569" }} />
                </InputAdornment>
              ),
            }}
          />
          {!isAdmin && (
            <Button
              variant="contained"
              size="small"
              startIcon={<AddRoundedIcon />}
              onClick={() => navigate(routeConfigs.newSellItem.path)}
              sx={{
                bgcolor: PRIMARY,
                color: "#FFFFFF",
                borderRadius: "8px",
                fontWeight: 600,
                fontFamily: BODY_FONT,
                px: 2,
                height: 36,
                boxShadow: "none",
                "&:hover": { bgcolor: PRIMARY_DARK, boxShadow: "none" },
              }}
            >
              Post new item
            </Button>
          )}
        </Box>
      </Box>
      <Box sx={{ flex: 1, minHeight: 0, width: "100%" }}>
        <DataGrid
          aria-label="My Sales"
          loading={listLoading}
          rows={rows}
          columns={columns}
          rowHeight={52}
          columnHeaderHeight={40}
          autoPageSize
          slots={{ pagination: SellGridPagination, loadingOverlay: BrandLoaderOverlay }}
          slotProps={{ pagination: { rowCount: totalRows } as any }}
          paginationMode="server"
          rowCount={totalRows}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          onRowSelectionModelChange={(ids: GridRowSelectionModel) => {
            console.log(ids);
          }}
          pageSizeOptions={[paginationModel.pageSize]}
          disableRowSelectionOnClick
          sx={{
            height: "100%",
            border: "none",
            fontFamily: BODY_FONT,
            fontSize: "12.5px",
            color: INK,
            [`& .${gridClasses.cell}:focus, & .${gridClasses.cell}:focus-within`]: {
              outline: "none",
            },
            [`& .${gridClasses.columnHeader}:focus, & .${gridClasses.columnHeader}:focus-within`]: {
              outline: "none",
            },
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: "#F1F5F9",
              borderBottom: `1px solid ${LINE}`,
              minHeight: "40px !important",
              maxHeight: "40px !important",
            },
            "& .MuiDataGrid-columnHeaderTitle": {
              fontFamily: MONO_FONT,
              fontSize: "10px",
              fontWeight: 600,
              letterSpacing: "0.05em",
              textTransform: "uppercase",
              color: INK,
            },
            "& .MuiDataGrid-cell": {
              display: "flex",
              alignItems: "center",
              borderBottom: `1px solid ${LINE_SOFT}`,
              overflow: "hidden",
              py: 0,
            },
            "& .MuiDataGrid-cellContent": {
              display: "flex",
              alignItems: "center",
              width: "100%",
              height: "100%",
              overflow: "hidden",
            },
            "& .MuiDataGrid-row": {
              transition: "background-color .15s ease",
            },
            "& .MuiDataGrid-row:hover": {
              backgroundColor: "#F1F5F9",
            },
            "& .MuiDataGrid-row:hover .stickyCol, & .MuiDataGrid-row.Mui-hovered .stickyCol": {
              backgroundColor: "#F1F5F9",
            },
            // Edit button fades in on row hover (always visible on touch / reduced motion).
            "& .rowActions": {
              opacity: 0.35,
              transition: "opacity .15s ease",
            },
            "& .MuiDataGrid-row:hover .rowActions, & .rowActions:focus-within": {
              opacity: 1,
            },
            "@media (hover: none), (prefers-reduced-motion: reduce)": {
              "& .rowActions": { opacity: 1, transition: "none" },
            },
            "& .monoCell": {
              fontFamily: MONO_FONT,
              fontSize: "11.5px",
              color: INK_SOFT,
            },
            "& .monoStrongCell": {
              fontFamily: MONO_FONT,
              fontSize: "11.5px",
              fontWeight: 600,
              color: INK,
            },
            "& .MuiDataGrid-footerContainer": {
              minHeight: 44,
              borderTop: `1px solid ${LINE}`,
              backgroundColor: "#FFFFFF",
            },
          }}
          columnVisibilityModel={{
            Pharmacy: !!isAdmin,
          }}
        />
      </Box>
    </Box>
  );
};

export default SellList;
